import { Suspense, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { useStores } from '@/hooks/useStores';
import { lazyRoute } from '@/routing/lazyRoute';
import { INVITATIONS_SETTINGS_SECTION } from './InvitationsWaitingBadge';

const InvitationWaitingToast = lazyRoute(
  () => import('@/features/access/InvitationWaitingToast'),
  (module) => module.InvitationWaitingToast,
);

/** Whether this address is Settings → Invitations, where the list itself says what is new. */
export function onInvitationsSettings(pathname: string, search: string): boolean {
  return (
    /\/settings\/?$/.test(pathname) &&
    new URLSearchParams(search).get('section') === INVITATIONS_SETTINGS_SECTION
  );
}

/**
 * I-2233 (`docs/concept-invitations-to-several-work-areas.md` AC-PI.4): when an invitation the person
 * has not been told about is waiting, a notice says so once, with View to Settings → Invitations.
 * Jay, 2026-10-27: "we can have a notification snack panel that leads to the settings view".
 *
 * Mounted once (`Layout.tsx`). Announcing marks the invitations seen with the person
 * (`InvitationsWaitingStore.announce`), so the notice does not come back on the next page load or on
 * another device. Nothing is announced while the page is hidden — the poller reads again when it is
 * shown — nor on Settings → Invitations, where the list is on screen. The toast's words load with it
 * (`InvitationWaitingToast`), not in the launch entry.
 */
export const InvitationWaitingNotice = observer(function InvitationWaitingNotice() {
  const store = useStores()?.invitationsWaitingStore;
  const { pathname, search } = useLocation();
  const unannounced = store?.unannounced;
  const quiet = onInvitationsSettings(pathname, search);
  useEffect(() => {
    if (!store || !unannounced || unannounced.length === 0) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    store.announce({ quiet });
  }, [store, unannounced, quiet]);
  // Opening Settings → Invitations while the notice is up: the list says it now.
  useEffect(() => {
    if (quiet) store?.dismissAnnouncement();
  }, [store, quiet]);
  if (!store?.announcement) return null;
  return (
    <Suspense fallback={null}>
      <InvitationWaitingToast />
    </Suspense>
  );
});
